import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { WalletCard } from '../components/wallet/WalletCard';
import { Logo } from '../components/shared/Logo';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { BottomNavigation } from '../components/navigation/BottomNavigation';
import { Wallet, Order, Transaction } from '../types';
import api, { formatCurrency } from '../services/api';
import './DashboardScreen.css';

export const DashboardScreen: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [pendingOrders, setPendingOrders] = useState<Order[]>([]);
  const [recentTransactions, setRecentTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      setError('');

      try {
        // Load wallets data
        const walletsData = await api.wallets.getWallets();
        setWallets(walletsData);

        // Load pending orders
        try {
          const ordersData = await api.orders.getOrders({ status: 'PENDING', limit: 5 });
          setPendingOrders(ordersData);
        } catch (err) {
          console.warn('No pending orders or orders endpoint not available');
          setPendingOrders([]);
        }

        // Load recent transactions
        try {
          const transactionsData = await api.transactions.getTransactions({ limit: 5 });
          setRecentTransactions(transactionsData);
        } catch (err) {
          console.warn('No transactions or transactions endpoint not available');
          setRecentTransactions([]);
        }

      } catch (err: any) {
        console.error('Failed to load dashboard data:', err);
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const getTotalBalance = () => {
    return wallets.reduce((sum, wallet) => sum + wallet.totalBalance, 0);
  };

  const getTotalAvailableBalance = () => {
    return wallets.reduce((sum, wallet) => sum + wallet.availableBalance, 0);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleWalletClick = (wallet: Wallet) => {
    navigate(`/wallet/${wallet.type.toLowerCase()}`);
  };

  const handlePendingOrdersClick = () => {
    navigate('/orders', { state: { filterToPending: true } });
  };

  const handleTransactionHistoryClick = () => {
    navigate('/transactions');
  };

  const handleSendMoneyClick = () => {
    navigate('/send-money');
  };

  const handleReceiveMoneyClick = () => {
    navigate('/receive-money');
  };

  const handleTopUpClick = () => {
    navigate('/top-up');
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  if (loading) {
    return (
      <div className="dashboard-screen">
        <LoadingSpinner message="Loading your dashboard..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-screen">
        <div className="error-container">
          <h2>Error Loading Dashboard</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-screen">
      <div className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <div className="user-greeting">
              <h1>Welcome back!</h1>
              {user?.name && <h2 className="user-name">{user.name}</h2>}
            </div>
          </div>
          <div className="header-actions">
            <Logo size="medium" variant="light" />
          </div>
        </div>
      </div>

      <div className="dashboard-main">
        {/* Total Balance Section */}
        <div className="total-balance-section">
          <div className="total-balance-card">
            <div className="balance-info">
              <div className="total-balance-row">
                <div className="balance-label">Total Balance</div>
                <h2 className="balance-amount total">{formatCurrency(getTotalBalance())}</h2>
              </div>
              <div className="available-balance-row">
                <div className="balance-label available">Available Balance</div>
                <h3 className="balance-amount available">{formatCurrency(getTotalAvailableBalance())}</h3>
              </div>
            </div>
            
            {/* Quick Actions */}
            <div className="quick-actions">
              <button className="action-button" onClick={handleSendMoneyClick}>
                <i className="fas fa-paper-plane"></i>
                <span>Send</span>
              </button>
              <button className="action-button" onClick={handleReceiveMoneyClick}>
                <i className="fas fa-qrcode"></i>
                <span>Receive</span>
              </button>
              <button className="action-button" onClick={handleTopUpClick}>
                <i className="fas fa-plus-circle"></i>
                <span>Top Up</span>
              </button>
            </div>
          </div>
        </div>

        {/* Pending Orders */}
        {pendingOrders.length > 0 && (
          <div className="pending-orders-section">
            <div className="pending-orders-banner" onClick={handlePendingOrdersClick}>
              <div className="banner-content">
                <i className="fas fa-shopping-cart"></i>
                <div>
                  <div className="banner-title">
                    {pendingOrders.length} Pending Order{pendingOrders.length > 1 ? 's' : ''}
                  </div>
                  <div className="banner-subtitle">
                    Total: {formatCurrency(pendingOrders.reduce((sum, order) => sum + order.totalAmount, 0))}
                  </div>
                </div>
              </div>
              <i className="fas fa-chevron-right"></i>
            </div>
          </div>
        )}

        {/* Wallets Section */}
        <div className="wallets-section">
          <div className="section-header">
            <h3>Your Wallets</h3>
          </div>
          <div className="wallets-grid">
            {wallets.map((wallet) => (
              <WalletCard
                key={wallet.id}
                wallet={wallet}
                onClick={() => handleWalletClick(wallet)}
              />
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="recent-activity-section">
          <div className="section-header">
            <h3>Recent Activity</h3>
            <button className="view-all-button" onClick={handleTransactionHistoryClick}>
              View All
            </button>
          </div>
          {recentTransactions.length > 0 ? (
            <div className="transactions-list">
              {recentTransactions.slice(0, 5).map((transaction) => (
                <div key={transaction.id} className="transaction-card">
                  <div className="transaction-header">
                    <div className="transaction-left">
                      <div className="transaction-icon-container">
                        <div className={`transaction-icon ${
                          transaction.type === 'DEBIT' || transaction.type === 'TRANSFER_OUT' ? 'transaction-debit' : 'transaction-credit'
                        }`}>
                          <i className={`fas ${
                            transaction.type === 'TOP_UP' ? 'fa-plus-circle' :
                            transaction.type === 'TRANSFER_OUT' ? 'fa-arrow-up' :
                            transaction.type === 'TRANSFER_IN' ? 'fa-arrow-down' :
                            transaction.type === 'DEBIT' ? 'fa-arrow-up' :
                            'fa-arrow-down'
                          }`}></i>
                        </div>
                      </div>
                      <div className="transaction-info">
                        <h4>{transaction.description}</h4>
                        <p className="transaction-description">
                          {wallets.find(w => w.id === transaction.walletId)?.type || 'Main'} Wallet
                        </p>
                      </div>
                    </div>
                    <div className={`transaction-status ${
                      transaction.status === 'PENDING' ? 'status-pending' :
                      transaction.status === 'COMPLETED' ? 'status-completed' :
                      transaction.status === 'FAILED' ? 'status-failed' :
                      'status-completed'
                    }`}>
                      {transaction.status || 'COMPLETED'}
                    </div>
                  </div>
                  
                  <div className="transaction-details">
                    <div>
                      <span className="amount-label">Amount</span>
                      <span className={`amount-value ${
                        transaction.type === 'DEBIT' || transaction.type === 'TRANSFER_OUT' ? 'debit' : 'credit'
                      }`}>
                        {transaction.type === 'DEBIT' || transaction.type === 'TRANSFER_OUT' ? '-' : '+'}
                        {formatCurrency(Math.abs(transaction.amount))}
                      </span>
                    </div>
                    <div>
                      <span className="date-label">Date</span>
                      <span className="date-value">
                        {new Date(transaction.createdAt || transaction.updatedAt).toLocaleDateString('en-ZA', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <i className="fas fa-receipt"></i>
              <p>No recent activity to show</p>
            </div>
          )}
        </div>
      </div>
      
      <BottomNavigation />
    </div>
  );
};